import React, { useState, useEffect, useRef } from 'react';
import { verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '@discovr/common';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, CircleNotch, CheckCircle, XCircle, Eye, EyeSlash, ArrowRight } from '../../design-system/icons';
import { usePasswordPolicy } from './usePasswordPolicy';
import { PasswordRequirements } from './PasswordRequirements';
import { resetPasswordAndSignIn } from './resetPasswordFlow';

export interface ResetPasswordPageProps {
  /** Called after successful reset + auto sign-in. Parent handles navigation. */
  onSuccess: () => void;
  logoSrc?: string;
  appName?: string;
  /**
   * Firebase password-reset code. Supplied by the route component from its
   * typed `Route.useSearch()`. Falls back to `window.location.search` only as
   * a defensive default for any non-routed consumer.
   */
  oobCode?: string;
}

export function ResetPasswordPage({
  onSuccess,
  logoSrc,
  appName = 'Discovr',
  oobCode: oobCodeProp,
}: ResetPasswordPageProps) {
  const oobCode =
    oobCodeProp ?? new URLSearchParams(window.location.search).get('oobCode') ?? '';

  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const ran = useRef(false);

  const {
    requirements,
    isValid: isPasswordValid,
    errorMessage: passwordError,
  } = usePasswordPolicy(password);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot ref-guarded mount effect that verifies the reset code over the network
    if (!oobCode) { setIsVerifying(false); return; }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => { setVerifiedEmail(email); setIsCodeValid(true); })
      .catch(() => setIsCodeValid(false))
      .finally(() => setIsVerifying(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid) { toast.error(passwordError ?? 'Please meet all password requirements.'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setIsSubmitting(true);
    try {
      await resetPasswordAndSignIn(auth, oobCode, password, verifiedEmail);
      setIsSuccess(true);
      toast.success('Password set! Signing you in…');
      onSuccess();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <CircleNotch size={32} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted px-4 py-12">
      <div className="w-full max-w-md rounded-md border border-border bg-card shadow-sm">
        <div className="rounded-t-md bg-black px-8 py-6">
          <div className="flex items-center gap-2">
            {logoSrc && <img src={logoSrc} alt={appName} className="size-6 object-contain" />}
            <span className="text-xl font-extrabold tracking-tight text-white">{appName}</span>
          </div>
          <p className="mt-1 text-sm text-white/70">Dashboard</p>
        </div>

        <div className="p-8">
          {isSuccess && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-success/10 ring-8 ring-success/5">
                <CheckCircle size={36} className="text-success" />
              </div>
              <h1 className="text-xl font-bold text-foreground">You're all set!</h1>
              <p className="text-sm text-muted-foreground">Signing you in…</p>
            </div>
          )}

          {!isSuccess && (!oobCode || !isCodeValid) && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-destructive/10 ring-8 ring-destructive/5">
                <XCircle size={36} className="text-destructive" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Link expired or already used</h1>
              <p className="mt-1 text-sm/relaxed text-muted-foreground">
                This link has expired or been used already.{' '}
                <a
                  href="mailto:hello@getdiscovr.ai"
                  className="font-semibold text-primary hover:text-primary/80"
                >
                  Contact support
                </a>{' '}
                and we'll send a fresh one.
              </p>
            </div>
          )}

          {!isSuccess && oobCode && isCodeValid && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Set your password</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Choose a password to get started.
                </p>
              </div>

              {verifiedEmail && (
                <div className="mb-6 flex items-center gap-2.5 rounded-md border border-border bg-muted px-4 py-3">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {verifiedEmail[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Account</p>
                    <p className="truncate text-sm font-medium text-foreground">{verifiedEmail}</p>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-pw">Password</Label>
                  <div className="relative">
                    <Lock size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="rp-pw"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      className="px-9"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute top-1/2 right-1 -translate-y-1/2 size-7 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeSlash size={15} /> : <Eye size={15} />}
                    </Button>
                  </div>
                  {password.length > 0 && (
                    <PasswordRequirements requirements={requirements} className="mt-1.5" />
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-confirm">Confirm password</Label>
                  <div className="relative">
                    <Lock size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="rp-confirm"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      className="px-9"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tabIndex={-1}
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute top-1/2 right-1 -translate-y-1/2 size-7 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirm ? 'Hide confirm' : 'Show confirm'}
                    >
                      {showConfirm ? <EyeSlash size={15} /> : <Eye size={15} />}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !isPasswordValid}
                  className="mt-1 w-full"
                >
                  {isSubmitting ? (
                    <CircleNotch size={16} className="mr-2 animate-spin" />
                  ) : (
                    <ArrowRight size={16} className="mr-2" />
                  )}
                  {isSubmitting ? 'Setting password…' : 'Set password & sign in'}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="rounded-b-md border-t border-border bg-muted px-8 py-4 text-center text-xs text-muted-foreground">
          This link is single-use and expires after 72 hours.
        </div>
      </div>
    </div>
  );
}
